import { apiClient } from './apiClient';

/**
 * PDF chunk metadata for a single chunk
 */
export interface PdfChunkDto {
  bookId: string;
  chunkIndex: number;
  startPage: number;
  endPage: number;
  pageCount: number;
  blobPath: string;
  /**
   * Id of the underlying SecureContentChunk row for this PDF chunk.
   * Used by the Flashcard Library tab to resolve the currently-viewed
   * page (or page range) to a concrete chunk Guid without issuing a
   * /search-in-book call.
   */
  secureContentChunkId: string;
}

/**
 * PDF book metadata with all chunks
 */
export interface PdfBookMetadataDto {
  bookId: string;
  totalPages: number;
  totalChunks: number;
  chunks: PdfChunkDto[];
}

/**
 * Text item in a page (with bounding box coordinates)
 */
export interface TextItemDto {
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
}

/**
 * Page text layer (for text selection and search)
 */
export interface PageTextDto {
  pageNumber: number;
  items: TextItemDto[];
}

/**
 * Text layer for a chunk (multiple pages)
 */
export interface PdfTextLayerDto {
  chunkIndex: number;
  pages: PageTextDto[];
}

/**
 * Service for fetching PDF chunks and metadata from backend.
 * Implements DRM-protected progressive PDF loading.
 */
class PdfChunkService {
  private readonly baseUrl = '/api/app/pdf-chunks';

  /**
   * Get PDF book metadata (total pages, chunk count, chunk mappings).
   * Call this first to initialize the PDF viewer.
   *
   * @param bookId - Book ID (GUID)
   * @returns PDF metadata with chunk information
   */
  async getMetadata(bookId: string): Promise<PdfBookMetadataDto> {
    const response = await apiClient.get<PdfBookMetadataDto>(`${this.baseUrl}/metadata`, {
      params: { bookId },
    });
    return response.data;
  }

  /**
   * Get a watermarked PDF chunk (5 pages).
   * Chunks are cached for 1 hour (HTTP cache headers).
   *
   * @param bookId - Book ID (GUID)
   * @param chunkIndex - Chunk index (0-based)
   * @returns PDF chunk bytes as ArrayBuffer
   */
  async getChunk(bookId: string, chunkIndex: number): Promise<ArrayBuffer> {
    const response = await apiClient.get(`${this.baseUrl}/chunk`, {
      params: { bookId, chunkIndex },
      responseType: 'arraybuffer',
    });
    return response.data;
  }

  /**
   * Get text layer for a PDF chunk (for text selection and search).
   * Text layers are cached for 1 hour (HTTP cache headers).
   *
   * @param bookId - Book ID (GUID)
   * @param chunkIndex - Chunk index (0-based)
   * @returns Text layer with bounding box coordinates
   */
  async getTextLayer(bookId: string, chunkIndex: number): Promise<PdfTextLayerDto> {
    const response = await apiClient.get<PdfTextLayerDto>(`${this.baseUrl}/text-layer`, {
      params: { bookId, chunkIndex },
    });
    return response.data;
  }

  /**
   * Get the chunk index for a given page number.
   *
   * @param metadata - PDF metadata
   * @param pageNumber - Page number (1-based)
   * @returns Chunk index (0-based)
   */
  getChunkIndexForPage(metadata: PdfBookMetadataDto, pageNumber: number): number {
    const chunk = metadata.chunks.find(
      (c) => pageNumber >= c.startPage && pageNumber <= c.endPage
    );
    return chunk ? chunk.chunkIndex : 0;
  }

  /**
   * Get the page offset within a chunk (for PDF.js rendering).
   *
   * @param metadata - PDF metadata
   * @param pageNumber - Page number (1-based)
   * @returns Page offset within chunk (0-based)
   */
  getPageOffsetInChunk(metadata: PdfBookMetadataDto, pageNumber: number): number {
    const chunk = metadata.chunks.find(
      (c) => pageNumber >= c.startPage && pageNumber <= c.endPage
    );
    return chunk ? pageNumber - chunk.startPage : 0;
  }
}

export const pdfChunkService = new PdfChunkService();
